import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, MapPin, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { TRUST } from "@/content";

const FALLBACK_EVENTS = [
  {
    badge: "દર ગુરુવારે",
    title: "નિ:શુલ્ક મેડિકલ કેમ્પ",
    desc: "ડોક્ટરો દ્વારા મફત તબીબી તપાસ અને મફત દવા વિતરણ — દરેક ગુરુવારે ટ્રસ્ટ કચેરી ખાતે.",
    place: "આનંદનગર, રાજકોટ",
  },
  {
    badge: "ઉત્તરાયણ · જાન્યુઆરી",
    title: "મહાપતંગ મહોત્સવ",
    desc: "1,000 થી વધુ દિવ્યાંગ બંધુઓ સાથે પતંગ ઉત્સવ — દર વર્ષે અવિસ્મરણીય ઉજવણી.",
    place: "રાજકોટ",
  },
  {
    badge: "નવરાત્રી",
    title: "ફન ક્લબ નવરાત્રી ઉત્સવ",
    desc: "દિવ્યાંગ બંધુઓ અને ભગિનીઓ માટે ગરબા તથા આનંદની ખાસ ઉજવણી.",
    place: "રાજકોટ",
  },
  {
    badge: "તારીખ જલ્દી જાહેર",
    title: "ખેલ મહાકુંભ — જિલ્લા સ્પર્ધા",
    desc: "રાજકોટ અને મોરબી જિલ્લાના દિવ્યાંગ ખેલાડીઓની સ્પર્ધાઓ — અમે અધિકૃત સંકલનકર્તા છીએ.",
    place: "રાજકોટ અને મોરબી",
  },
  {
    badge: "વાર્ષિક",
    title: "હરિદ્વાર યાત્રા",
    desc: "દિવ્યાંગ બંધુઓ માટે તીર્થયાત્રાની વિશેષ વ્યવસ્થા — સંપૂર્ણ સાથે-સગવડ સાથે.",
    place: "હરિદ્વાર",
  },
  {
    badge: "વાર્ષિક",
    title: "સ્નેહ મિલન",
    desc: "આખા ટ્રસ્ટ પરિવારનું વાર્ષિક મેળાવડું, સન્માન સમારંભ અને ભોજન.",
    place: "રાજકોટ",
  },
];

export default function Events() {
  const [events, setEvents] = useState(FALLBACK_EVENTS);

  useEffect(() => {
    api
      .getEvents()
      .then((list) => {
        if (Array.isArray(list) && list.length > 0) setEvents(list);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="events" data-testid="events-section" className="py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mb-12"
        >
          <p className="font-head font-bold text-sm tracking-[0.2em] text-terracotta mb-4 flex items-center gap-2">
            <CalendarDays className="w-4 h-4" />
            આવનાર કાર્યક્રમો
          </p>
          <h2 className="font-display text-ink text-4xl sm:text-5xl leading-snug">
            ઉત્સવ અને <span className="text-saffron">સેવાના દિવસો</span>
          </h2>
          <p className="mt-5 text-ink/70 text-base md:text-lg leading-relaxed">
            કેમ્પ, યાત્રા અને ઉત્સવો — જ્યાં દરેક દિવ્યાંગ બંધુનું સ્વાગત છે.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {events.map((e, i) => (
            <motion.article
              key={e.id || e.title}
              data-testid={`event-card-${i}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              className="group rounded-2xl border border-stone-200 bg-cream p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_16px_40px_rgb(0,0,0,0.08)] transition-all duration-200"
            >
              {e.photo && (
                <img
                  src={api.mediaFileUrl(e.photo)}
                  alt={e.title}
                  loading="lazy"
                  className="w-full h-40 object-cover rounded-xl mb-4"
                />
              )}
              <span className="inline-block rounded-full bg-saffron-soft border border-saffron/30 px-4 py-1.5 font-head font-bold text-xs text-saffron-dark mb-4">
                {e.badge}
              </span>
              <h3 className="font-head font-bold text-ink text-xl mb-2.5">
                {e.title}
              </h3>
              <p className="text-ink/65 text-sm leading-relaxed mb-4">{e.desc}</p>
              <p className="flex items-center gap-2 font-head font-semibold text-xs text-leaf-dark">
                <MapPin className="w-3.5 h-3.5" />
                {e.place}
              </p>
            </motion.article>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-10 flex flex-wrap items-center gap-2 font-head text-sm text-ink/60"
        >
          <Phone className="w-4 h-4 text-saffron" />
          ચોક્કસ તારીખ અને નોંધણી માટે સંપર્ક કરો:
          <a
            data-testid="events-contact-link"
            href={`tel:${TRUST.contact.phones[0].replace(/\s/g, "")}`}
            className="font-bold text-terracotta hover:text-saffron transition-colors"
          >
            {TRUST.contact.phones[0]}
          </a>
        </motion.p>
      </div>
    </section>
  );
}
